/* A coding session as its own thread, opened from its line in the agent's
 * conversation (or the pill above its composer) the way a message to a coworker opens: over the thread,
 * the session as a conversation, its composer pinned to the bottom. No header bar: a pill floats at the
 * top ("Bo ⇄ Claude Code" and the session's title) with the transcript scrolling under it, and a labelled
 * "Back to Bo" beside it. The owner types into the session here; everyone else reads it behind a lock
 * line. Back and Escape return to the conversation (Escape not while a draft is in the composer). */
import type { CodingSession } from "../cloud/coding-session.ts";
import type { HostedStaffAgent } from "../cloud/types.ts";
import { agentFigure } from "./avatar.ts";
import {
	type CodingSessionApi,
	CodingSessionElement,
	type CodingSessionNames,
	HARNESS_NAMES,
	harnessBadge,
} from "./coding-session.ts";
import { h } from "./dom.ts";
import { matchesKey } from "./keys.ts";
import { t } from "./preferences.ts";

export interface CodingSessionThreadOptions {
	agent: HostedStaffAgent | undefined;
	names: (session: CodingSession) => CodingSessionNames;
	viewerOwns: (session: CodingSession) => boolean;
	api: CodingSessionApi;
	signal: AbortSignal;
	askAgent?: (text: string) => void;
	close: () => void;
}

const BACK_GLYPH =
	'<path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>';
const SWAP_GLYPH =
	'<path d="M7 7h11m0 0l-3-3m3 3l-3 3M17 17H6m0 0l3 3m-3-3l3-3" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>';

function glyph(markup: string, size: number, className: string): SVGSVGElement {
	const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
	svg.setAttribute("viewBox", "0 0 24 24");
	svg.setAttribute("width", String(size));
	svg.setAttribute("height", String(size));
	svg.setAttribute("aria-hidden", "true");
	svg.setAttribute("class", className);
	svg.innerHTML = markup;
	return svg;
}

export class CodingSessionThreadView {
	readonly el: HTMLElement;
	readonly element: CodingSessionElement;
	readonly #lifetime = new AbortController();
	readonly #close: () => void;

	constructor(session: CodingSession, options: CodingSessionThreadOptions) {
		this.#close = options.close;
		const signal = AbortSignal.any([options.signal, this.#lifetime.signal]);
		const names = options.names(session);
		const harness = HARNESS_NAMES[session.harness];
		this.element = new CodingSessionElement(session, {
			agent: options.agent,
			names: options.names,
			viewerOwns: options.viewerOwns,
			api: options.api,
			signal,
			askAgent: options.askAgent,
		});
		const back = h(
			"button",
			{
				type: "button",
				class: "coding-thread-back",
				title: t("Back to the conversation"),
				onclick: () => this.close(),
			},
			glyph(BACK_GLYPH, 16, "icon"),
			h("span", null, t("Back to {name}", { name: names.agent })),
		);
		const header = h(
			"header",
			{ class: "coding-thread-header" },
			back,
			h(
				"div",
				{ class: "coding-thread-identity", title: `${names.agent} ⇄ ${harness} · ${session.title}` },
				agentFigure(options.agent, names.agent, { size: 22 }),
				glyph(SWAP_GLYPH, 12, "coding-thread-swap"),
				h("span", { class: "coding-thread-harness" }, harnessBadge(session.harness)),
				h("h1", null, `${names.agent} ⇄ ${harness}`),
				h("span", { class: "coding-thread-subtitle" }, session.title),
			),
		);
		this.el = h(
			"section",
			{
				class: "coding-thread",
				role: "dialog",
				// A click on something that takes no focus (a choice in Safari or Firefox) leaves it here, not on the
				// page's body, so Escape still reaches the thread.
				tabindex: "-1",
				"aria-modal": "false",
				"aria-label": `${names.agent} ⇄ ${harness} · ${session.title}`,
				onkeydown: (event: KeyboardEvent) => {
					if (!matchesKey(event, "dismiss")) return;
					// Escape in the composer keeps a draft being written; the back arrow still leaves.
					if (event.target instanceof HTMLTextAreaElement && event.target.value.trim()) {
						event.stopPropagation();
						return;
					}
					event.preventDefault();
					event.stopPropagation();
					this.close();
				},
			},
			header,
			this.element.el,
		);
		if (options.signal.aborted) this.dispose();
		else
			options.signal.addEventListener("abort", () => this.dispose(), { once: true, signal: this.#lifetime.signal });
	}

	/** A fresh listing from the conversation's poll, handed to the session's own element. */
	update(session: CodingSession): void {
		this.element.update(session);
	}

	/** Over the conversation, with the caret in the composer when the viewer may type now; otherwise, and on a
	 * touch screen (where the caret would open the keyboard over a session opened to be read), on the back arrow. */
	attach(body: HTMLElement): void {
		body.append(this.el);
		const touch = typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
		if (touch || !this.element.focusComposer())
			this.el.querySelector<HTMLButtonElement>(".coding-thread-back")?.focus();
	}

	close(): void {
		if (this.#lifetime.signal.aborted) return;
		this.dispose();
		this.#close();
	}

	dispose(): void {
		if (this.#lifetime.signal.aborted) return;
		this.#lifetime.abort();
		this.element.dispose();
		this.el.remove();
	}
}
